import { Button } from "@mui/material";
import { GridRenderCellParams } from "@mui/x-data-grid";
import { Chip, Container, Icon, Tooltip, TypoGraph } from "components";
import { carrierStatus } from "constants/status";
import colors from 'theme/colors'
import { numberOrStringComparator, stringDateComparator } from "tools/comparators";
import strings from "strings";

const t = strings.TABLE_DEFAULTS

const myCarrierList = {
    myCarrierColumn: [
      {
        field: 'carrierId',
        headerName: 'ID',
        width: 70,
        minWidth: 70,
      },
      {
        field: 'carrierName',
        sortable: true,
        headerName: 'Carrier Name',
        minWidth: 200,
        flex: 1,
        handleClick: (row: any) => {},
        renderCell: function (params: GridRenderCellParams) {
          return (
            <Tooltip
              title={params.value}
              containerClassName="ellipsis"
              maxWidth={210}
            >
              <TypoGraph
                id={"carrierName_" + params.row.carrierId}
                component="span"
                variant="body1"
                color={colors.primary}
                onClick={() => this.handleClick(params.row)}
                link
              >
                {params.value}
              </TypoGraph>
            </Tooltip>
          )
        },
      },
      {
        field: 'firstName',
        headerName: 'First Name',
        sortable: true,
        width: 90,
        minWidth: 80,
      },
      {
        field: 'lastName',
        headerName: 'Last Name',
        sortable: true,
        width: 90,
        minWidth: 80,
      },
      {
        field: 'email',
        headerName: 'Email',
        sortable: true,
        width: 150,
        minWidth: 200,
        flex: 0.8,
        handleClick: (row: any) => {},
        renderCell: function (params: GridRenderCellParams) {
          return (
            <Tooltip
              title={params.value}
              containerClassName="ellipsis"
              maxWidth={250}
            >
              <TypoGraph
                component="span"
                variant="body2"
                onClick={() => this.handleClick(params.row)}
                link
              >
                {params.value}
              </TypoGraph>
            </Tooltip>
          );
        },
      },      
      {
        field: 'dot',
        headerName: 'DOT',
        sortable: true,
        sortComparator: numberOrStringComparator,
        width: 85,
        minWidth: 85,
        flex: 0.2,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Tooltip
              title={params.value}
              containerClassName="ellipsis"
              maxWidth={210}
            >
              {params.value}
            </Tooltip>
          )
        },
      },
      {
        field: 'cuid',
        headerName: 'CUID',
        sortable: true,
        width: 85,
        minWidth: 85,
        flex: 0.2,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Tooltip
              title={params.value}
              containerClassName="ellipsis"
              maxWidth={210}
            >
              {params.value}
            </Tooltip>
          )
        },
      },
      {
        field: 'policyId',
        headerName: 'Policy ID',
        sortable: true,
        width: 85,
        minWidth: 85,
        flex: 0.2,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Tooltip
              title={params.value}
              containerClassName="ellipsis"
              maxWidth={210}
            >
              {params.value}
            </Tooltip>
          )
        },
      },
      {
        field: 'eldVendorDisplayName',
        headerName: 'ELD Provider',
        sortable: true,
        minWidth: 140,
        flex: 0.5,
        renderCell: (params: GridRenderCellParams) => {
          const provider = params?.row?.eldVendorDisplayName
          if (!params?.row?.confidentialEvents) {
            return (
              <Tooltip
                title={provider}
                containerClassName="ellipsis"
                maxWidth={210}
              >
                {provider}
              </Tooltip>
            )
          }
          return (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <div style={{ lineHeight: 1.3 }}>
                <Tooltip
                  title={provider}
                  containerClassName="ellipsis"
                  maxWidth={210}
                >
                  {provider || t.DASH_VALUE}
                </Tooltip>
                <Tooltip title={strings.CARRIERS.CONFIDENTIAL_EVENTS_GUIDANCE}>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={strings.CARRIERS.CONFIDENTIAL_EVENTS}
                    sx={{
                      display: 'flex',
                      width: 'fit-content',
                      mt: '3px',
                      height: '16px',
                      fontSize: '10px',
                      fontWeight: 600,
                      borderRadius: '4px',
                      borderColor: '#F5C6C0',
                      backgroundColor: '#FDECEA',
                      color: '#C62828',
                      '& .MuiChip-label': { px: '6px' },
                    }}
                  />
                </Tooltip>
              </div>
            </div>
          )
        },
      },
      {
        field: 'numberOfTrucks',
        headerName: 'Truck',
        sortable: true,
        sortComparator: numberOrStringComparator,
        width: 70,
        minWidth: 70,
        flex: 0.2,
      },
      {
        field: 'numberOfDrivers',
        headerName: 'Trailer',
        sortable: true,
        sortComparator: numberOrStringComparator,
        width: 70,
        minWidth: 70,
        flex: 0.2,
      },
      {
        field: 'statusInfo',
        headerName: 'Onboard Date',
        sortable: true,
        sortComparator: stringDateComparator,
        width: 100,
        minWidth: 100,
        flex: 0.4,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Tooltip
              title={params.value}
              containerClassName="ellipsis"
              maxWidth={210}
            >
              {params.value}
            </Tooltip>
          )
        },
      },
      {
        field: 'credentialStatus',
        headerName: 'Status',
        sortable: true,
        minWidth: 120,
        width: 120,
        flex: 0.4,
        renderCell: function (params: GridRenderCellParams) {
          return (
            <Tooltip
              title={carrierStatus[params?.row?.credentialStatus]?.label ?? t.DASH_VALUE}
              containerClassName="ellipsis"
              maxWidth={110}
            >
              <Chip
                size="small"
                label={carrierStatus[params?.row?.credentialStatus]?.label ?? t.DASH_VALUE}
                variant="filled"
                sx={{
                  backgroundColor: carrierStatus[params?.row?.credentialStatus]?.background ?? '#E3E6E8',
                  color: carrierStatus[params?.row?.credentialStatus]?.color ?? '#707A8B',
                }}
              />
            </Tooltip>
          )
        },
      },
      {
        field: 'data',
        headerName: 'T&C',
        sortable: false,
        width: 60,
        minWidth: 60,
        flex: 0.2,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Container
              sx={{
                ml: '0',
              }}
            >
              <Tooltip
                title={'Terms & Conditions'}
                containerClassName="ellipsis"
                maxWidth={80}
              >
                <Button
                  onClick={() => window.open(params.row?.letterOfAuthorizationUrl, '_blank')}
                  className={
                    params.row?.letterOfAuthorizationUrl ? 'termsAndConditionButton' : 'inactiveButton'
                  }
                  disabled={!params.row?.letterOfAuthorizationUrl}
                >
                  <Icon
                    materialIcon="PictureAsPdf"
                    size={24}
                  />
                </Button>
              </Tooltip>
            </Container>
          )
        },
      },
    ],
  }

const actionList = [
  {
    name: 'Edit',
    icon: 'icon_edit',
    iconSize: 35,
  },
  {
    name: 'Pause',
    icon: 'icon_pause',
    iconSize: 35,
    isDisabled: (params: any) => {
      const status = params?.credentialStatus?.toUpperCase()
      return status !== 'ACTIVE' && status !== 'DISCONNECTED'
    }
  },
  {
    name: 'Resume',
    icon: 'icon_resume',
    iconSize: 35,
    isDisabled: (params: any) => {
      const status = params?.credentialStatus?.toUpperCase()
      return status !== 'PAUSED'
    }
  },
  {
    name: 'Offboard',
    icon: 'icon_offboard',
    iconSize: 35,
  },
  {
    name: 'Reconnect',
    icon: 'icon_reconnect',
    iconSize: 35,
    isDisabled: (params: any) => {
      const status = params?.credentialStatus?.toUpperCase()
      return status !== 'DISCONNECTED'
    }
  },
];

export {
    myCarrierList,
    actionList
}